import { useProvideInject } from "./provideInjectGenerator";
import {
  AnalyticEvents,
  CardAnalyticsRequest,
  CardAnalyticsContext,
  Card,
  CardsContext
} from "@fresh8/types/src/analytics";
import { SportEvent } from "@fresh8/types/src/betData";
import {
  AdTagCampaign,
  AdTagExperiment,
  AdTagExperimentVariant
} from "@fresh8/types/src/index";
import { BetData, Market } from "@fresh8/types/src/betData";
import { computed, Ref, ComputedRef } from "vue";
import { AdTag, CardTypes, CasinoDataSuccess } from "@fresh8/types/src/index";
import { MarketCard } from "../composition/card-creation/types";
import { v4 as uuidv4 } from "uuid";

/**
 * Defines a new uuid for each time we load the page.
 * This viewID should be the same while it's valid.
 */
const viewIDv4 = uuidv4();

export function createCardAnalyticsContext(
  betData: Ref<BetData | undefined>,
  adTag: Ref<AdTag>,
  statsURL: string,
  selectedCampaign?: Ref<AdTagCampaign | undefined>,
  experiment?: Ref<AdTagExperiment | undefined>,
  experimentVariant?: Ref<AdTagExperimentVariant | undefined>
): ComputedRef<CardAnalyticsContext | undefined> {
  const analyticsContext = computed(() => {
    const analytics = betData.value?.analytics;

    // BUG Adtag is a ref so this will always be true... what is it were trying to do here??
    const useCardAnalytics = adTag !== undefined;

    const urlParams = new URLSearchParams(window.location.search);
    const customField1 = urlParams.get("customField1") || undefined;
    const customField2 = urlParams.get("customField2") || undefined;
    const customField3 = urlParams.get("customField3") || undefined;
    const customField4 = urlParams.get("customField4") || undefined;
    const customField5 = urlParams.get("customField5") || undefined;
    const analyticsDisabled = urlParams.get("analyticsDisabled") || undefined;

    let analyticsEnabled: boolean;

    if (analyticsDisabled) {
      analyticsEnabled = analyticsDisabled !== "true";
    } else {
      analyticsEnabled = process.env.NODE_ENV === "production";
    }

    const pageUrl = urlParams.get("ref") || window.location.href;
    const campaign = selectedCampaign?.value;

    return {
      emit: analyticsEnabled,
      enabled: useCardAnalytics,
      catalogueID: analytics?.catalogueId || "",
      viewID: viewIDv4,
      operator: adTag.value?.operator.key || "",
      statsURL: statsURL,
      pageUrl,
      campaignID: campaign?._id || "",
      // window.adtagId is only set in prod and staging
      // The anaytlics object may not load so we want to fallback to use the window
      adtagID: window.adtagId || adTag.value?._id || "",
      brandID: adTag?.value?.brand._id || "",
      customField: {
        customField1,
        customField2,
        customField3,
        customField4,
        customField5
      },
      experimentID: experiment?.value?._id,
      experimentVariant: experimentVariant?.value?.id,
      experimentType: experiment?.value?.type,
      recommenderBehaviour:
        betData.value?.analytics?.recommenderBehaviour || "",
      recommenderDetail: betData.value?.analytics?.recommenderDetail || ""
    };
  });

  return analyticsContext;
}

const cardMap: Partial<Record<
  CardTypes,
  ({
    casinoData
  }: {
    casinoData: CasinoDataSuccess;
    card: MarketCard;
    betData: BetData;
  }) => Partial<Card>
>> = {
  offer: () => ({
    type: "offer",
    sport: "Offer",
    competition: "",
    betType: ""
  }),
  parlay: ({ card, betData }) => {
    const sets = card.marketIds.reduce<{
      sport: Set<string>;
      competition: Set<string>;
    }>(
      (acc, id) => {
        const market = getMarket(betData, [id]);
        const event = getEvent(betData, market);

        acc.sport.add(event?.sport as string);
        acc.competition.add(event?.competition as string);

        return acc;
      },
      { sport: new Set(), competition: new Set() }
    );

    const sportArr = Array.from(sets.sport);
    const competitionArr = Array.from(sets.competition);

    const sport: string = sportArr.length > 1 ? "Multiple Sports" : sportArr[0];
    const competition: string =
      competitionArr.length > 1 ? "Multiple Competitions" : competitionArr[0];

    return {
      type: "parlay",
      sport,
      competition,
      betType: ""
    };
  },
  casinoHighlight: ({ casinoData }) => ({
    type: "casinoHighlight",
    sport: "Casino",
    competition: casinoData?.data?.highlight?.casino_game_segment ?? "",
    eventName: casinoData?.data?.highlight?.casino_game_name ?? ""
  }),
  casinoList: () => ({
    type: "casinoList",
    sport: "Casino",
    competition: "",
    betType: ""
  }),
  mainMarket: () => ({
    type: "mainMarket",
    betType: ""
  })
};

function getCreateCardFunction(cardType: CardTypes) {
  return cardMap?.[cardType];
}

export function createCardsContext(
  betData: Ref<BetData | undefined>,
  cards: ComputedRef<MarketCard[]>,
  casinoData: Ref<CasinoDataSuccess>
): ComputedRef<CardsContext | undefined> {
  return computed(() => {
    const populatedCards = cards.value.map(card => {
      const market = getMarket(betData.value, card.marketIds);
      const event = getEvent(betData.value, market);
      const createCard = getCreateCardFunction(card.type);

      const generalValues = {
        type: card.type,
        sport: event?.sport ?? "",
        competition: event?.competition ?? "",
        betType: market?.marketType ?? "",
        eventName: event?.name,
        ...(createCard?.({
          casinoData: casinoData.value,
          card,
          betData: betData.value!
        }) ?? {})
      };

      return {
        ...generalValues,
        srTournamentID: event?.tournament_id,
        srMatchID: event?.match_id,
        srHomeID: event?.competitors?.home?.competitor_id,
        srAwayID: event?.competitors?.away?.competitor_id
      };
    });
    return {
      cards: populatedCards
    };
  });
}
/**
 * returns the bet event
 * @param  {BetData | undefined} betData
 * @return {marketIds} marketIds
 */
function getEvent(
  betData: BetData | undefined,
  market: Market | undefined
): SportEvent | undefined {
  if (betData === undefined || market === undefined) {
    return undefined;
  }

  return betData.events[market.eventId];
}

/**
 * returns the bet event
 * @param  {BetData | undefined} betData
 * @return {marketIds} marketIds
 */
function getMarket(
  betData: BetData | undefined,
  marketIds: string[]
): Market | undefined {
  if (betData === undefined || marketIds.length === 0) {
    return undefined;
  }
  return betData.markets[marketIds[0]];
}

const CardAnalyticsSymbol = Symbol("cardAnalyticsProvider");
const { injector, provider } = useProvideInject<CardAnalyticsContext>(
  CardAnalyticsSymbol,
  {} as CardAnalyticsContext
);

const useCardAnalyticsContext = injector;
export const provideCardAnalyticsContext = provider;

export const CardsSymbol = Symbol("cardsContext");
export const {
  provider: provideCardsContext,
  injector: injectCardsContext
} = useProvideInject<CardsContext>(CardsSymbol, {
  cards: []
});

/**
 * makeRequest base64 encodes the data, sends a request using fetch and
 * returns a promise.
 * @param  {String}   URL        is the end point to make the request
 * @param  {Object}   dataObject is the data payload
 * @return {AxiosPromise}
 */
async function emitRequest(URL: string, dataObject: CardAnalyticsRequest) {
  const searchParams = new URLSearchParams(window.location.search);

  const headers = {};
  if (searchParams.get("testAnalytics")) {
    headers["F8-T"] = true;
  }

  await fetch(URL + "/e/", {
    method: "POST",
    mode: "cors", // no-cors, *cors, same-origin
    cache: "default",
    credentials: "include",
    body: JSON.stringify(dataObject),
    headers: headers
  });
}

/**
 * @param {*} eventName
 * @param {*} eventDescription
 * @param {EventData} eventData
 * @param {String} eventData.viewId - View ID
 * @param {Boolean} eventData.shouldEmit - View ID
 * @param {Pixels} eventData.pixels - View ID
 * @param {String} eventData.statsURL - View ID
 * @param {String} eventData.product - View ID
 * @param {String} eventData.productType - View ID
 */
export function useHandleCardAnalytics(): (
  statsEventName: AnalyticEvents,
  statsEventDescription: string,
  cards?: Card[],
  cardNumber?: string,
  optionName?: string
) => Promise<CardAnalyticsRequest | undefined> {
  const cardAnalyticsContext = useCardAnalyticsContext();
  const cardsContext = injectCardsContext();

  return async function $handleCardAnalytics(
    statsEventName: AnalyticEvents,
    statsEventDescription: string,
    cards?: Card[],
    cardNumber?: string,
    optionName?: string
  ) {
    if (cards === undefined) {
      cards = cardsContext.value.cards;
    }

    if (cardAnalyticsContext.value && cardAnalyticsContext.value.enabled) {
      const {
        viewID,
        emit,
        statsURL,
        catalogueID,
        pageUrl,
        operator = "",
        customField = {},
        adtagID,
        campaignID,
        brandID,
        experimentID,
        experimentVariant,
        experimentType,
        recommenderBehaviour,
        recommenderDetail
      } = cardAnalyticsContext.value;

      const shouldEmit = emit;
      const card1 = getCardData(cards, 0);
      const card2 = getCardData(cards, 1);
      const card3 = getCardData(cards, 2);
      const card4 = getCardData(cards, 3);
      const card5 = getCardData(cards, 4);
      const statsEventCard = getCardNumber(cardNumber);
      const statsEventOptionName = optionName || "";
      const eventData: CardAnalyticsRequest = {
        viewID,
        operator,
        recommenderBehaviour,
        recommenderDetail,
        statsEventName,
        statsEventDescription,
        statsEventCard,
        statsEventOptionName,
        adScreenHeight: window.screen.availHeight,
        adScreenWidth: window.screen.availWidth,
        pageUrl: pageUrl.length <= 1500 ? pageUrl : pageUrl.substring(0, 1500),
        catalogueID,
        adtagID,
        campaignID,
        brandID,
        card1,
        card2,
        card3,
        card4,
        card5,
        experimentID,
        experimentVariant,
        experimentType,
        cardCount: cards.length
      };

      // Add the custom labels to the payload
      for (const customFieldKey of Object.keys(customField)) {
        if (customField[customFieldKey]) {
          eventData[customFieldKey] = customField[customFieldKey];
        }
      }

      if (!shouldEmit) {
        // eslint-disable-next-line no-console
        console.info(
          "Card Analytics request data:\n" + JSON.stringify(eventData, null, 2)
        );
        return eventData;
      }
      const promises: Array<Promise<void>> = [emitRequest(statsURL, eventData)];
      try {
        await Promise.all(promises);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      return eventData;
    }
  };
}

/**
 * returns the card data for the specific card number
 * @param  {Object} cards
 * @param  {number} cardNumber
 * @return {Card} the Card data
 */
function getCardData(
  cards: Card[] | undefined,
  cardNumber: number
): Card | undefined {
  if (cards === undefined || cards.length < cardNumber) {
    return;
  }
  if (cards[cardNumber] === undefined) {
    return;
  }

  return cards[cardNumber];
}

/**
 * returns the card number
 * @param  {string} cardNumber
 * @return {string} the card number string
 */
function getCardNumber(cardNumber: string | undefined): string {
  if (cardNumber === undefined || cardNumber === "") {
    return "";
  }

  return "card" + cardNumber;
}
